import isEqual from 'react-fast-compare';
import { call, delay, put, putResolve, select, spawn, takeEvery } from 'typed-redux-saga';

import { fpsAPI, dictionaryAPI, iframeAPI } from '@/api';
import { SbpInvoiceAPIModelStatusEnum } from '@/generated/api/fps/core';
import { goalReached, YMGoals } from '@/infrastructure/ym';
import * as global from '@/model/global/actions';
import { makeSelectIsFrameView } from '@/model/global/selectors';
import { withGlobalLock } from '@/model/global/utils';
import { takeEveryTypesafe } from '@/utils/redux';

import * as actions from './actions';
import { makeSelectDetails, makeSelectProcessingStatus } from './selectors';
import { InvoiceDetails, InvoiceDetailsLoadError, InvoiceProcessingStatus } from './types';

let pollInProgress = false;
let expirationCheckInProgress = false;

const selectDetails = makeSelectDetails();
const selectStatus = makeSelectProcessingStatus();
const selectIsFrame = makeSelectIsFrameView();

function* pollInvoiceStatus(): Generator {
  if (pollInProgress) {
    return;
  }
  pollInProgress = true;
  try {
    yield* spawn(refreshExpiration);
    while (true) {
      const newDetails: InvoiceDetails = yield* call(fpsAPI.queryInvoice);
      const savedDetails = (yield* select(selectDetails))?.value;
      if (!isEqual(newDetails, savedDetails)) {
        yield* putResolve(actions.storeInvoice({ details: newDetails }).pure);
      }
      yield* delay(5000);
      const status: InvoiceProcessingStatus | undefined = yield* select(selectStatus);
      if (status !== InvoiceProcessingStatus.IN_PROGRESS) {
        const isFrame = yield* select(selectIsFrame);
        if (isFrame) {
          if (status === InvoiceProcessingStatus.SUCCEEDED) {
            goalReached(YMGoals.INVOICE_SUCCESS);
            iframeAPI.sendInvoiceFinished('SUCCESS');
          } else if (status === InvoiceProcessingStatus.EXPIRED || status === InvoiceProcessingStatus.FAILED) {
            if (status === InvoiceProcessingStatus.EXPIRED) {
              goalReached(YMGoals.INVOICE_EXPIRED);
            } else {
              goalReached(YMGoals.INVOICE_ERROR);
            }
            iframeAPI.sendInvoiceFinished('FAILED');
          }
        }
        break;
      }
    }
  } finally {
    pollInProgress = false;
  }
}

function* refreshExpiration(): Generator {
  if (expirationCheckInProgress) {
    return;
  }
  expirationCheckInProgress = true;
  try {
    while (true) {
      yield* put(actions.storeStatus().pure);
      const status: InvoiceProcessingStatus | undefined = yield* select(selectStatus);
      yield* delay(1000);
      if (status !== InvoiceProcessingStatus.IN_PROGRESS) {
        break;
      }
    }
  } finally {
    expirationCheckInProgress = false;
  }
}

function* createInvoice(): Generator {
  try {
    const details: InvoiceDetails = yield* call(fpsAPI.createInvoice);
    yield* put(actions.storeInvoice({ details }).pure);
    yield* spawn(pollInvoiceStatus);
    goalReached(YMGoals.INVOICE_INIT);
  } catch (e) {
    if (e.response?.data?.code === 'no_phones_in_pool') {
      yield* put(actions.storeInvoice({ error: InvoiceDetailsLoadError.NO_PHONE_IN_POOL }).pure);
    } else {
      yield* put(actions.storeInvoice({ error: InvoiceDetailsLoadError.COMMON }).pure);
      throw e;
    }
  }
}

let initializing = false;

function* init(): Generator {
  if (initializing) {
    return;
  }
  initializing = true;
  try {
    let state: actions.StoreInitActionPayload = {};

    try {
      state = { ...state, banks: (yield* call(() => dictionaryAPI.queryBanks(false))).filter(({ id }) => id !== '_') };
    } catch (e) {
      // do nothing
    }

    try {
      state = { ...state, limits: yield* call(fpsAPI.queryLimits) };
    } catch (e) {
      // do nothing
    }
    try {
      const details: InvoiceDetails = yield* call(fpsAPI.queryInvoice);
      state = { ...state, details };
    } catch (e) {
      // do nothing
    }
    yield* putResolve(actions.storeInitialized(state).pure);
    if (
      // prettier-ignore
      // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
      state.details?.status === SbpInvoiceAPIModelStatusEnum.InProgress
      || state.details?.status === SbpInvoiceAPIModelStatusEnum.Created
    ) {
      yield* spawn(pollInvoiceStatus);
    }
  } finally {
    initializing = false;
    yield put(actions.notifyInitializationFinished().pure);
  }
}

function* logout(): Generator {
  yield* put(actions.storeLogout().pure);
}

export default function* rootSaga(): Generator {
  yield* takeEveryTypesafe(actions.init, withGlobalLock(init, 'common.messages.operations.init'));
  yield* takeEveryTypesafe(actions.createInvoice, withGlobalLock(createInvoice, 'common.messages.operations.init'));
  yield* takeEvery(global.notifyLogout.type, withGlobalLock(logout, 'common.messages.operations.logout'));
}
